import { useEffect, useState } from "react";
import { Users, Share2, EyeOff, FileDown, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { API } from "@/lib/api";
import { money, compact } from "@/components/equity/AssumptionsPanel";
import { formatDate } from "@/lib/format";

export function TeamEquityOverview({ companyId, onShare, refreshKey }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/companies/${companyId}/equity-overview`).then(({ data }) => setData(data)).catch(() => setData({ rows: [], totals: {} }));
  useEffect(() => { if (companyId) load(); }, [companyId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const { rows, totals, company } = data;
  return (
    <Card className="rounded-xl border bg-card p-5" data-testid="team-equity-overview">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Users className="h-4 w-4 text-primary" /> Team equity overview</h2>
          <p className="text-xs text-muted-foreground">Every grant valued with today's settings. Spot who has never opened their page.</p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={load} data-testid="team-equity-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5" data-testid="team-equity-totals">
        {[["Grants", totals.grants], ["Options", Math.round(totals.options || 0).toLocaleString()], ["Vested value", money(totals.intrinsic_vested)],
          ["Fair value (BS)", money(totals.black_scholes_total)], ["Pool left", Math.round(company?.pool_remaining || 0).toLocaleString()]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-secondary/60 p-2.5"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div><div className="mt-0.5 font-semibold tabular-nums text-foreground">{v}</div></div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="team-equity-empty">No option grants yet. Issue an options security to see it here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-equity-table">
            <thead><tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-3">Employee</th><th className="py-2 pr-3 text-right">Options</th><th className="py-2 pr-3 text-right">Vested</th><th className="py-2 pr-3 text-right">Vested value</th>
              <th className="py-2 pr-3 text-right">Fair value</th><th className="py-2 pr-3 text-right">Break-even</th><th className="py-2 pr-3">Page</th><th className="py-2" />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`team-equity-row-${r.id}`}>
                  <td className="py-2.5 pr-3"><div className="font-semibold text-foreground">{r.holder_name}</div><div className="text-[11px] text-muted-foreground">{r.holder_email || "no email"} · {r.option_type} @ {money(r.strike, 2)}</div></td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{Math.round(r.options).toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{Math.round(r.vested_fraction * 100)}%</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums font-semibold text-foreground">{money(r.intrinsic_vested)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{money(r.black_scholes_total)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{r.break_even != null ? compact(r.break_even) : "-"}</td>
                  <td className="py-2.5 pr-3">
                    {!r.shared ? <Badge variant="secondary" className="font-normal">not shared</Badge>
                      : r.view_count === 0 ? <Badge className="gap-1 border-amber-200 bg-amber-50 font-normal text-amber-800" data-testid={`team-equity-never-${r.id}`}><EyeOff className="h-3 w-3" /> never viewed</Badge>
                      : <span className="text-xs text-muted-foreground">viewed ×{r.view_count} · {formatDate(r.last_viewed_at)}</span>}
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button asChild variant="ghost" size="sm" className="h-7 gap-1 text-xs"><a href={`${API}/securities/${r.id}/statement.pdf`} target="_blank" rel="noreferrer" data-testid={`team-equity-pdf-${r.id}`}><FileDown className="h-3.5 w-3.5" /> PDF</a></Button>
                      <Button variant={r.shared ? "secondary" : "outline"} size="sm" className="h-7 gap-1 text-xs" onClick={() => onShare(r.id)} data-testid={`team-equity-share-${r.id}`}><Share2 className="h-3.5 w-3.5" /> {r.shared ? "Manage" : "Share"}</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
